import { Button } from '@/app/common/components/button/Button'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import usePostSystemCatalogItem from '@/queries/system_catalog/hooks/usePostSystemCatalogItem'
import { SystemCatalogCategory } from '@/queries/system_catalog/system_catalog.schema'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { CategorySelector } from './_components/CategorySelector/CategorySelector'
import {
  removeCategory,
  toggleCategory,
} from './_components/CategorySelector/CategorySelector.utils'
import { ItemNameField } from './_components/ItemNameField'
import ItemTypeSelector from './_components/ItemTypeSelector'
import { AddItemFormSchema, AddItemFormValues } from './item-form.schema'
import { parseRequestError } from '@/app/common/utilities'

interface AddItemFormContainerProps {
  categories: SystemCatalogCategory[]
  onAddItemSuccess: () => void
}

function AddItemPopup({
  categories,
  onAddItemSuccess,
}: AddItemFormContainerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()

  const { control, resetField, getValues, handleSubmit, watch } =
    useForm<AddItemFormValues>({
      resolver: zodResolver(AddItemFormSchema),
      defaultValues: {
        categories: [],
        name: '',
        type: 'Good',
      },
    })

  const systemCatalogMutation = usePostSystemCatalogItem({
    onSuccess: () => {
      toast({
        variant: 'default',
        title: 'Item successfully Added',
        description: 'The item has been added to the system catalog.',
      })
      onAddItemSuccess()
      resetField('name')
      setIsOpen(false)
      resetField('categories')
    },
    onError: (error) => {
      const errorMsg = parseRequestError(error)

      toast({
        variant: 'destructive',
        title: 'Error adding item',
        description: errorMsg.message,
      })
    },
  })

  const options = categories
    .filter((item) => {
      console.log({
        item: item.type_name,
        form_type: getValues().type,
      })
      return item.type_name === getValues().type
    })
    .map((category) => {
      return {
        value: category.id.toString(),
        label: category.name,
      }
    })

  const onSubmit = handleSubmit((data) => {
    systemCatalogMutation.mutate({
      name: data.name,
      type: data.type,
      categories: data.categories,
    })
  })

  watch('type')
  watch('categories')

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant={'primary'}>Add item</Button>
      </DialogTrigger>
      <DialogContent className="space-y-4">
        <h2 className="text-lg font-medium">Add New Item</h2>
        <form onSubmit={onSubmit} className="space-y-3">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState }) => {
              return (
                <ItemNameField
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )
            }}
          />
          <Controller
            control={control}
            name="type"
            render={({ field }) => {
              return (
                <ItemTypeSelector
                  onBlur={field.onBlur}
                  value={field.value}
                  onChange={field.onChange}
                />
              )
            }}
          />
          <Controller
            control={control}
            name="categories"
            render={({ field, fieldState }) => {
              return (
                <CategorySelector
                  error={fieldState.error?.message}
                  removeCategory={(item) => {
                    const newCategories = removeCategory(
                      getValues().categories,
                      item
                    )
                    field.onChange(newCategories)
                  }}
                  options={options}
                  handleSelect={(item) => {
                    const newCategories = toggleCategory(
                      getValues().categories,
                      item
                    )
                    field.onChange(newCategories)
                  }}
                  selectedCategories={field.value}
                />
              )
            }}
          />
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              onClick={() => {
                setIsOpen(false)
              }}
              variant={'outline'}
            >
              Cancel
            </Button>
            <div>
              <SubmitButton
                status={systemCatalogMutation.status}
                buttonTxt={'Add Item'}
              />
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export { AddItemPopup }
